import axios, { AxiosResponse } from 'axios';
import { 
  HierarchicalMappingResponse, 
  FrameworkComparisonResponse, 
  CoverageReport 
} from '../types/hierarchical';
import type {
  Document, DocumentProcessingResponse, ProcessedDocumentResponse,
  DocumentEntity, DocumentTable, DocumentReference, DocumentTopic,
  DocumentSummary, DocumentListItem, EntityFilters, TableFilters, ReferenceFilters,
  DocumentSection
} from '../types/document';
import {
  AUTEndpoint,
  AUTVersion,
  Project,
  TestCase,
  TestExecution,
  TestCaseGenerationRequest,
  TestCaseGenerationResponse,
  TestCaseGenerationWithTechniquesResponse,
  TestExecutionRequest,
  TestExecutionBatchResponse,
  CustomerSupportTestRequest,
  CustomerSupportTestResponse,
  TestStatistics,
  FormattedEvaluation,
  // NEW hierarchical types
  Domain,
  DomainCreate,
  DomainUpdate,
  DomainListResponse,
  Category,
  CategoryCreate,
  CategoryUpdate,
  CategoryListResponse,
  Technique,
  TechniqueCreate,
  TechniqueUpdate,
  TechniqueListResponse,
  TechniqueGroupedResponse,
  // LEGACY types (kept for backward compatibility)
  TestType,
  TestTypeCreate,
  TestTypeUpdate,
  TestTypeListResponse,
  TestTypeCategoryResponse,
  TestTypeCategory,
  TestTechnique,
  TestTechniqueCreate,
  TestTechniqueUpdate,
  TestTechniqueListResponse,
  TestTechniqueGroupedResponse,
  EvaluationMapping,
  EvaluationMappingCreate,
  EvaluationMappingUpdate,
  TechniqueMappingResponse,
  EvaluationMappingListResponse,
  EvaluationFramework,
  EvaluationFrameworkCreate,
  EvaluationFrameworkUpdate,
  FrameworkMetric,
  FrameworkMetricCreate,
  FrameworkMetricUpdate,
  FrameworkWithMetrics,
  FrameworkListResponse,
  FrameworkStatistics,
  Organization,
  OrganizationSettings,
  OrganizationSettingsCreate,
  OrganizationSettingsUpdate,
  User,
  UserCreate,
  UserCreateWithFirebase,
  UserUpdate,
  UserProjectAssignment,
  UserSync
} from '../types';
import { config } from '../config';

// Configure axios base URL (see src/config.ts and REACT_APP_API_URL)
const API_BASE_URL = config.apiBaseUrl;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

export const setAuthToken = (token: string | null) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Helper function to check if error is a network error
const isNetworkError = (error: any): boolean => {
  return (
    error.code === 'ERR_NETWORK' ||
    error.code === 'ERR_CONNECTION_REFUSED' ||
    error.code === 'ECONNREFUSED' ||
    (error.message && error.message.includes('Network Error')) ||
    (error.response === undefined && error.request !== undefined)
  );
};

// Helper function to retry requests on network errors
export const retryRequest = async <T>(
  requestFn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error: any) {
      lastError = error;
      
      // Only retry on network errors
      if (!isNetworkError(error) || attempt === maxRetries) {
        throw error;
      }
      
      // Exponential backoff: 1s, 2s, 4s
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    // Log network errors with more context
    if (isNetworkError(error)) {
      console.error('API Network Error:', {
        code: error.code,
        message: error.message,
        url: error.config?.url,
        method: error.config?.method,
        timestamp: new Date().toISOString()
      });
      
      // Provide user-friendly error message
      error.userMessage = 'Unable to connect to the backend server. Please ensure the backend is running.';
    } else {
      console.error('API Error:', error);
    }
    
    return Promise.reject(error);
  }
);

// API service for projects
export const projectsApi = {
  list: async (organizationId: number): Promise<Project[]> => {
    const response = await api.get(`/api/organizations/${organizationId}/projects/`);
    return response.data;
  },

  create: async (organizationId: number, project: Omit<Project, 'id' | 'created_at' | 'updated_at' | 'organization_id'>): Promise<Project> => {
    const response = await api.post(`/api/organizations/${organizationId}/projects/`, { ...project, status: project.status || 'active', organization_id: organizationId });
    return response.data;
  },

  get: async (organizationId: number, id: number): Promise<Project> => {
    const response = await api.get(`/api/organizations/${organizationId}/projects/${id}`);
    return response.data;
  },

  update: async (organizationId: number, id: number, project: Partial<Omit<Project, 'id' | 'organization_id' | 'created_at' | 'updated_at'>>): Promise<Project> => {
    const response = await api.put(`/api/organizations/${organizationId}/projects/${id}`, project);
    return response.data;
  },

  delete: async (organizationId: number, id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/organizations/${organizationId}/projects/${id}`);
    return response.data;
  },
};

// API service for AUT endpoints
export const autEndpointsApi = {
  list: async (organizationId: number, projectId: number): Promise<AUTEndpoint[]> => {
    const response = await api.get(`/api/organizations/${organizationId}/projects/${projectId}/endpoints/`);
    return response.data;
  },

  create: async (organizationId: number, projectId: number, endpoint: { name: string; environment?: string; base_url?: string; auth_type?: string; auth_header_name?: string; auth_token?: string; description?: string }): Promise<AUTEndpoint> => {
    const payload = { ...endpoint, project_id: projectId };
    const response = await api.post(`/api/organizations/${organizationId}/projects/${projectId}/endpoints/`, payload);
    return response.data;
  },

  get: async (organizationId: number, projectId: number, id: number): Promise<AUTEndpoint> => {
    const response = await api.get(`/api/organizations/${organizationId}/projects/${projectId}/endpoints/${id}`);
    return response.data;
  },

  update: async (organizationId: number, projectId: number, id: number, endpoint: Partial<Pick<AUTEndpoint, 'name' | 'environment' | 'base_url' | 'auth_type' | 'auth_header_name' | 'description'>> & { auth_token?: string }): Promise<AUTEndpoint> => {
    const response = await api.put(`/api/organizations/${organizationId}/projects/${projectId}/endpoints/${id}`, endpoint);
    return response.data;
  },

  delete: async (organizationId: number, projectId: number, id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/organizations/${organizationId}/projects/${projectId}/endpoints/${id}`);
    return response.data;
  },
};

// API service for versions
export const versionsApi = {
  list: async (organizationId: number, projectId: number): Promise<AUTVersion[]> => {
    const response = await api.get(`/api/organizations/${organizationId}/projects/${projectId}/versions/`);
    return response.data;
  },

  create: async (organizationId: number, projectId: number, version: { version_name: string; description?: string; is_active?: boolean; aut_endpoint_id: number }): Promise<AUTVersion> => {
    const payload = { ...version, project_id: projectId };
    const response = await api.post(`/api/organizations/${organizationId}/projects/${projectId}/versions/`, payload);
    return response.data;
  },

  get: async (organizationId: number, projectId: number, id: number): Promise<AUTVersion> => {
    const response = await api.get(`/api/organizations/${organizationId}/projects/${projectId}/versions/${id}`);
    return response.data;
  },

  update: async (organizationId: number, projectId: number, id: number, version: Partial<Omit<AUTVersion, 'id' | 'created_at' | 'project_id'>>): Promise<AUTVersion> => {
    const response = await api.put(`/api/organizations/${organizationId}/projects/${projectId}/versions/${id}`, version);
    return response.data;
  },

  activate: async (organizationId: number, projectId: number, id: number): Promise<{ message: string }> => {
    const response = await api.put(`/api/organizations/${organizationId}/projects/${projectId}/versions/${id}/activate`);
    return response.data;
  },

  delete: async (organizationId: number, projectId: number, id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/organizations/${organizationId}/projects/${projectId}/versions/${id}`);
    return response.data;
  },
};

// API service for test cases
export const testCasesApi = {
  list: async (organizationId: number, projectId: number, sourceType?: string, category?: string, status?: string): Promise<TestCase[]> => {
    const params: any = {
      organization_id: organizationId,
      project_id: projectId
    };
    if (sourceType) params.source_type = sourceType;
    if (category) params.category = category;
    if (status) params.status = status;
    
    const response = await api.get('/api/test-cases/', { params });
    return response.data;
  },

  create: async (testCase: Omit<TestCase, 'id' | 'created_at'>): Promise<TestCase> => {
    const response = await api.post('/api/test-cases/', testCase);
    return response.data;
  },

  get: async (id: number): Promise<TestCase> => {
    const response = await api.get(`/api/test-cases/${id}`);
    return response.data;
  },

  update: async (id: number, testCase: Omit<TestCase, 'id' | 'created_at'>): Promise<TestCase> => {
    const response = await api.put(`/api/test-cases/${id}`, testCase);
    return response.data;
  },

  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/test-cases/${id}`);
    return response.data;
  },

  approve: async (id: number): Promise<{ message: string; status: string }> => {
    const response = await api.patch(`/api/test-cases/${id}/approve`);
    return response.data;
  },

  reject: async (id: number): Promise<{ message: string; status: string }> => {
    const response = await api.patch(`/api/test-cases/${id}/reject`);
    return response.data;
  },

  updateStatus: async (id: number, status: string): Promise<{ message: string; status: string }> => {
    const response = await api.patch(`/api/test-cases/${id}/status`, { status });
    return response.data;
  },

  generate: async (request: TestCaseGenerationRequest & { organization_id?: number; project_id?: number }): Promise<TestCaseGenerationResponse> => {
    const response = await api.post('/api/test-cases/generate', request);
    return response.data;
  },

  uploadPdf: async (file: File, organizationId: number, projectId: number, numTestCases: number = 5): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('num_test_cases', numTestCases.toString());
    formData.append('organization_id', organizationId.toString());
    formData.append('project_id', projectId.toString());
    
    const response = await api.post('/api/test-cases/upload-pdf', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getCategories: async (): Promise<string[]> => {
    const response = await api.get('/api/test-cases/categories');
    return response.data;
  },

  analyzeDocument: async (file: File): Promise<{
    success: boolean;
    analysis: {
      summary: string;
      reasoning: string;
      applicable_test_types: Array<{
        id: number;
        name: string;
        purpose?: string;
        prompt?: string;
      }>;
    };
    file_name: string;
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/api/test-cases/analyze-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  generateWithTestTypes: async (
    file: File, 
    organizationId: number, 
    projectId: number, 
    testTypeIds: number[], 
    techniqueIds?: number[], 
    numTestCases: number = 5
  ): Promise<TestCaseGenerationWithTechniquesResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('organization_id', organizationId.toString());
    formData.append('project_id', projectId.toString());
    formData.append('test_type_ids', testTypeIds.join(','));
    if (techniqueIds && techniqueIds.length > 0) {
      formData.append('technique_ids', techniqueIds.join(','));
    }
    formData.append('num_test_cases', numTestCases.toString());
    
    const response = await api.post('/api/test-cases/generate-with-test-types', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
};

// API service for tests
export const testsApi = {
  runBatch: async (organizationId: number, request: TestExecutionRequest): Promise<TestExecutionBatchResponse> => {
    // Use a longer timeout for batch test execution (5 minutes) since it can involve multiple tests and evaluations
    const response = await api.post(`/api/organizations/${organizationId}/tests/run`, request, {
      timeout: 300000 // 5 minutes (300 seconds)
    });
    return response.data;
  },

  listExecutions: async (organizationId: number, projectId?: number, versionId?: number, testCaseId?: number): Promise<TestExecution[]> => {
    const params: any = {};
    if (projectId) params.project_id = projectId;
    if (versionId) params.version_id = versionId;
    if (testCaseId) params.test_case_id = testCaseId;
    const response = await api.get(`/api/organizations/${organizationId}/tests/executions`, { params });
    return response.data;
  },

  getStatistics: async (organizationId: number, projectId?: number, versionId?: number): Promise<TestStatistics> => {
    const params: any = {};
    if (projectId) params.project_id = projectId;
    if (versionId) params.version_id = versionId;
    const response = await api.get(`/api/organizations/${organizationId}/tests/statistics`, { params });
    return response.data;
  },

  listEvaluations: async (organizationId: number, projectId?: number, versionId?: number, source?: string): Promise<FormattedEvaluation[]> => {
    const params: any = {};
    if (projectId) params.project_id = projectId;
    if (versionId) params.version_id = versionId;
    if (source) params.source = source;
    const response = await api.get(`/api/organizations/${organizationId}/tests/evaluations`, { params });
    return response.data;
  },

  runSingleTest: async (request: CustomerSupportTestRequest): Promise<CustomerSupportTestResponse> => {
    const response = await api.post('/api/tests/run-single', request);
    return response.data;
  },
};

// API service for test types
export const testTypesApi = {
  list: async (params?: {
    skip?: number;
    limit?: number;
    search?: string;
    category?: string;
    status?: string;
  }): Promise<TestTypeListResponse> => {
    const response = await api.get('/api/test-types/', { params });
    return response.data;
  },

  create: async (testType: TestTypeCreate): Promise<TestType> => {
    const response = await api.post('/api/test-types/', testType);
    return response.data;
  },

  get: async (id: number): Promise<TestType> => {
    const response = await api.get(`/api/test-types/${id}`);
    return response.data;
  },

  update: async (id: number, testType: TestTypeUpdate): Promise<TestType> => {
    const response = await api.put(`/api/test-types/${id}`, testType);
    return response.data;
  },

  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/test-types/${id}`);
    return response.data;
  },

  getCategories: async (): Promise<TestTypeCategoryResponse> => {
    const response = await api.get('/api/test-types/categories');
    return response.data;
  },
};

// API service for test techniques
export const testTechniquesApi = {
  list: async (params?: {
    skip?: number;
    limit?: number;
    search?: string;
    test_type_id?: number;
    status?: string;
  }): Promise<TestTechniqueListResponse> => {
    const response = await api.get('/api/test-techniques/', { params });
    return response.data;
  },

  create: async (technique: TestTechniqueCreate): Promise<TestTechnique> => {
    const response = await api.post('/api/test-techniques/', technique);
    return response.data;
  },

  get: async (id: number): Promise<TestTechnique> => {
    const response = await api.get(`/api/test-techniques/${id}`);
    return response.data;
  },

  update: async (id: number, technique: TestTechniqueUpdate): Promise<TestTechnique> => {
    const response = await api.put(`/api/test-techniques/${id}`, technique);
    return response.data;
  },

  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/test-techniques/${id}`);
    return response.data;
  },

  listGrouped: async (params?: {
    status?: string;
    search?: string;
  }): Promise<TestTechniqueGroupedResponse[]> => {
    const response = await api.get('/api/test-techniques/grouped', {
      params: {
        status: params?.status,
        search: params?.search,
        skip: 0,
        limit: 1000
      }
    });
    return response.data.test_techniques;
  },
};

// API service for evaluation mappings
export const evaluationMappingsApi = {
  getAllMappings: async (params?: {
    status?: string;
    search?: string;
  }): Promise<TechniqueMappingResponse[]> => {
    const response = await api.get('/api/evaluation-mappings/mappings', { params });
    return response.data;
  },

  createMapping: async (mapping: EvaluationMappingCreate): Promise<EvaluationMapping> => {
    const response = await api.post('/api/evaluation-mappings/mappings', mapping);
    return response.data;
  },

  updateMapping: async (mappingId: number, mapping: EvaluationMappingUpdate): Promise<EvaluationMapping> => {
    const response = await api.put(`/api/evaluation-mappings/mappings/${mappingId}`, mapping);
    return response.data;
  },

  deleteMapping: async (mappingId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/evaluation-mappings/mappings/${mappingId}`);
    return response.data;
  },

  getTechniqueMappings: async (techniqueId: number): Promise<EvaluationMapping[]> => {
    const response = await api.get(`/api/evaluation-mappings/mappings/technique/${techniqueId}`);
    return response.data;
  },

  getAvailableMetrics: async (): Promise<string[]> => {
    const response = await api.get('/api/evaluation-mappings/mappings/metrics');
    return response.data;
  },

  createBulkMappings: async (mappings: EvaluationMappingCreate[]): Promise<EvaluationMapping[]> => {
    const response = await api.post('/api/evaluation-mappings/mappings/bulk', mappings);
    return response.data;
  },
};

// NEW HIERARCHICAL API: Domains
export const domainsApi = {
  list: async (): Promise<DomainListResponse> => {
    const response = await api.get('/api/domains/');
    return response.data;
  },

  get: async (id: number): Promise<Domain> => {
    const response = await api.get(`/api/domains/${id}`);
    return response.data;
  },

  create: async (domain: DomainCreate): Promise<Domain> => {
    const response = await api.post('/api/domains/', domain);
    return response.data;
  },

  update: async (id: number, domain: DomainUpdate): Promise<Domain> => {
    const response = await api.put(`/api/domains/${id}`, domain);
    return response.data;
  },

  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/domains/${id}`);
    return response.data;
  },
};

// NEW HIERARCHICAL API: Categories (replaces TestTypes)
export const categoriesApi = {
  list: async (params?: {
    domain_id?: number;
    search?: string;
    page?: number;
    size?: number;
  }): Promise<CategoryListResponse> => {
    const response = await api.get('/api/categories/', { params });
    return response.data;
  },

  get: async (id: number): Promise<Category> => {
    const response = await api.get(`/api/categories/${id}`);
    return response.data;
  },

  create: async (category: CategoryCreate): Promise<Category> => {
    const response = await api.post('/api/categories/', category);
    return response.data;
  },

  update: async (id: number, category: CategoryUpdate): Promise<Category> => {
    const response = await api.put(`/api/categories/${id}`, category);
    return response.data;
  },

  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/categories/${id}`);
    return response.data;
  },
};

/** Category signal requirements (uses authenticated axios — do not use raw fetch). */
export const signalMappingsApi = {
  list: async (params?: { category_id?: number; signal_name?: string; is_required?: boolean }) => {
    const response = await api.get('/api/signal-mappings/', { params });
    return response.data;
  },
  statisticsOverview: async (): Promise<Record<string, unknown>> => {
    const response = await api.get('/api/signal-mappings/statistics/overview');
    return response.data;
  },
  create: async (payload: {
    category_id: number;
    signal_name: string;
    min_count?: number;
    ideal_count?: number | null;
    weight?: number;
    is_required?: boolean;
    description?: string | null;
  }) => {
    const response = await api.post('/api/signal-mappings/', payload);
    return response.data;
  },
  update: async (
    id: number,
    payload: {
      min_count?: number;
      ideal_count?: number | null;
      weight?: number;
      is_required?: boolean;
      description?: string | null;
    }
  ) => {
    const response = await api.put(`/api/signal-mappings/${id}`, payload);
    return response.data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/signal-mappings/${id}`);
  },
};

// NEW HIERARCHICAL API: Techniques (replaces TestTechniques)
export const techniquesApi = {
  list: async (params?: {
    category_id?: string;
    domain_id?: number;
    status?: string;
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<TechniqueListResponse> => {
    const response = await api.get('/api/techniques/', { params });
    return response.data;
  },

  listGrouped: async (params?: {
    domain_id?: number;
    status?: string;
    search?: string;
  }): Promise<TechniqueGroupedResponse[]> => {
    const response = await api.get('/api/techniques/grouped', { params });
    return response.data;
  },

  get: async (id: number): Promise<Technique> => {
    const response = await api.get(`/api/techniques/${id}`);
    return response.data;
  },

  create: async (technique: TechniqueCreate): Promise<Technique> => {
    const response = await api.post('/api/techniques/', technique);
    return response.data;
  },

  update: async (id: number, technique: TechniqueUpdate): Promise<Technique> => {
    const response = await api.put(`/api/techniques/${id}`, technique);
    return response.data;
  },

  delete: async (id: number): Promise<{ message: string}> => {
    const response = await api.delete(`/api/techniques/${id}`);
    return response.data;
  },
};

// Health check
export const healthApi = {
  check: async (): Promise<{ status: string; service: string }> => {
    const response = await api.get('/health');
    return response.data;
  },
};

// API service for evaluation frameworks
export const evaluationFrameworksApi = {
  // Framework management
  getAllFrameworks: async (params?: {
    skip?: number;
    limit?: number;
    search?: string;
    framework_type?: string;
    status?: string;
  }): Promise<FrameworkListResponse> => {
    const response = await api.get('/api/evaluation-frameworks/frameworks', { params });
    return response.data;
  },

  getFramework: async (id: number): Promise<EvaluationFramework> => {
    const response = await api.get(`/api/evaluation-frameworks/frameworks/${id}`);
    return response.data;
  },

  getFrameworkWithMetrics: async (id: number): Promise<FrameworkWithMetrics> => {
    const response = await api.get(`/api/evaluation-frameworks/frameworks/${id}/metrics`);
    return response.data;
  },

  createFramework: async (framework: EvaluationFrameworkCreate): Promise<EvaluationFramework> => {
    const response = await api.post('/api/evaluation-frameworks/frameworks', framework);
    return response.data;
  },

  updateFramework: async (id: number, framework: EvaluationFrameworkUpdate): Promise<EvaluationFramework> => {
    const response = await api.put(`/api/evaluation-frameworks/frameworks/${id}`, framework);
    return response.data;
  },

  deleteFramework: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/evaluation-frameworks/frameworks/${id}`);
    return response.data;
  },

  // Metric management
  getFrameworkMetrics: async (frameworkId: number): Promise<FrameworkMetric[]> => {
    const response = await api.get(`/api/evaluation-frameworks/frameworks/${frameworkId}/metrics`);
    return response.data;
  },

  createFrameworkMetric: async (frameworkId: number, metric: FrameworkMetricCreate): Promise<FrameworkMetric> => {
    const response = await api.post(`/api/evaluation-frameworks/frameworks/${frameworkId}/metrics`, metric);
    return response.data;
  },

  updateFrameworkMetric: async (metricId: number, metric: FrameworkMetricUpdate): Promise<FrameworkMetric> => {
    const response = await api.put(`/api/evaluation-frameworks/metrics/${metricId}`, metric);
    return response.data;
  },

  deleteFrameworkMetric: async (metricId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/evaluation-frameworks/metrics/${metricId}`);
    return response.data;
  },

  // Metadata and utilities
  getFrameworkTypes: async (): Promise<string[]> => {
    const response = await api.get('/api/evaluation-frameworks/frameworks/types');
    return response.data;
  },

  getMetricTypes: async (): Promise<string[]> => {
    const response = await api.get('/api/evaluation-frameworks/metrics/types');
    return response.data;
  },

  getFrameworkStatuses: async (): Promise<string[]> => {
    const response = await api.get('/api/evaluation-frameworks/frameworks/statuses');
    return response.data;
  },

  getMetricStatuses: async (): Promise<string[]> => {
    const response = await api.get('/api/evaluation-frameworks/metrics/statuses');
    return response.data;
  },

  getFrameworkStatistics: async (): Promise<FrameworkStatistics> => {
    const response = await api.get('/api/evaluation-frameworks/frameworks/statistics');
    return response.data;
  },

  // Get active frameworks for project assignment
  getActiveFrameworks: async (): Promise<EvaluationFramework[]> => {
    const response = await api.get('/api/evaluation-frameworks/frameworks', { 
      params: { status: 'active', limit: 100 } 
    });
    return response.data.frameworks || [];
  },
};

// Hierarchical Evaluation Mappings API
export const hierarchicalMappingsApi = {
  getHierarchicalView: async (): Promise<HierarchicalMappingResponse> => {
    const response = await api.get('/api/evaluation-mappings/hierarchical-view');
    return response.data;
  },

  getFrameworkComparison: async (frameworkIds?: string): Promise<FrameworkComparisonResponse> => {
    const params = frameworkIds ? { framework_ids: frameworkIds } : {};
    const response = await api.get('/api/evaluation-mappings/framework-comparison', { params });
    return response.data;
  },

  getCoverageReport: async (): Promise<CoverageReport> => {
    const response = await api.get('/api/evaluation-mappings/coverage-report');
    return response.data;
  },
};

// Excel Evaluations API
export const evaluationsApi = {
  getMetrics: async (): Promise<{ success: boolean; metrics: Array<{ value: string; name: string; description: string }> }> => {
    const response = await api.get('/api/evaluations/metrics');
    return response.data;
  },

  uploadExcel: async (organizationId: number, projectId: number, autVersionId: number, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post(`/api/organizations/${organizationId}/projects/${projectId}/versions/${autVersionId}/evaluations/upload-excel`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  configureColumnMapping: async (organizationId: number, projectId: number, autVersionId: number, config: {
    evaluation_run_id: number;
    column_mapping: Record<string, string | null>;
  }): Promise<any> => {
    const response = await api.post(`/api/organizations/${organizationId}/projects/${projectId}/versions/${autVersionId}/evaluations/configure-column-mapping`, config);
    return response.data;
  },

  configureEvaluation: async (organizationId: number, projectId: number, autVersionId: number, config: {
    evaluation_run_id: number;
    selected_metrics: string[];
    llm_model: string;
  }): Promise<any> => {
    const response = await api.post(`/api/organizations/${organizationId}/projects/${projectId}/versions/${autVersionId}/evaluations/configure-evaluation`, config);
    return response.data;
  },

  runEvaluation: async (organizationId: number, projectId: number, autVersionId: number, runId: number): Promise<any> => {
    const response = await api.post(`/api/organizations/${organizationId}/projects/${projectId}/versions/${autVersionId}/evaluations/run-evaluation/${runId}`);
    return response.data;
  },

  getEvaluationRuns: async (organizationId: number, projectId: number, autVersionId: number, page: number = 1, size: number = 10): Promise<any> => {
    const response = await api.get(`/api/organizations/${organizationId}/projects/${projectId}/versions/${autVersionId}/evaluations/runs`, {
      params: { page, size }
    });
    return response.data;
  },

  getEvaluationRun: async (organizationId: number, projectId: number, autVersionId: number, runId: number): Promise<any> => {
    const response = await api.get(`/api/organizations/${organizationId}/projects/${projectId}/versions/${autVersionId}/evaluations/runs/${runId}`);
    return response.data;
  },

  deleteEvaluationRun: async (organizationId: number, projectId: number, autVersionId: number, runId: number): Promise<any> => {
    const response = await api.delete(`/api/organizations/${organizationId}/projects/${projectId}/versions/${autVersionId}/evaluations/runs/${runId}`);
    return response.data;
  },

  getEvaluationResults: async (organizationId: number, projectId: number, autVersionId: number, runId: number): Promise<any> => {
    const response = await api.get(`/api/organizations/${organizationId}/projects/${projectId}/versions/${autVersionId}/evaluations/runs/${runId}/results`);
    return response.data;
  },
};

export default api;

// Organizations API
export const organizationsApi = {
  list: async (): Promise<Organization[]> => {
    const response = await api.get('/api/organizations/');
    return response.data;
  },
  create: async (org: { name: string; description?: string; status?: string }): Promise<Organization> => {
    const response = await api.post('/api/organizations/', org);
    return response.data;
  },
  get: async (id: number): Promise<Organization> => {
    const response = await api.get(`/api/organizations/${id}`);
    return response.data;
  },
  update: async (id: number, org: { name?: string; description?: string; status?: string }): Promise<Organization> => {
    const response = await api.put(`/api/organizations/${id}`, org);
    return response.data;
  },
  delete: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/organizations/${id}`);
    return response.data;
  },
};

// Current user context (organization + default project for header and localStorage)
export interface MeContextResponse {
  organization_id: number;
  organization_name: string;
  default_project: { id: number; name: string } | null;
  /** Application role from DB (used with Firebase claims for admin UI). */
  role?: string;
}

export const meApi = {
  getContext: async (): Promise<MeContextResponse> => {
    const response = await api.get<MeContextResponse>('/api/me');
    return response.data;
  },
  setDefaultProject: async (projectId: number): Promise<{ message: string; project_id: number; project_name: string }> => {
    const response = await api.put('/api/me/default-project', { project_id: projectId });
    return response.data;
  },
};

// Users API (Cognito-backed, org-scoped)
export const usersApi = {
  me: async (organizationId: number): Promise<User> => {
    const response = await api.get(`/api/organizations/${organizationId}/users/me`);
    return response.data;
  },
  list: async (organizationId: number, projectId?: number, isActive?: boolean): Promise<User[]> => {
    const params: any = {};
    if (projectId !== undefined) params.project_id = projectId;
    if (isActive !== undefined) params.is_active = isActive;
    const response = await api.get(`/api/organizations/${organizationId}/users/`, { params });
    return response.data;
  },
  get: async (organizationId: number, userId: number): Promise<User> => {
    const response = await api.get(`/api/organizations/${organizationId}/users/${userId}`);
    return response.data;
  },
  create: async (organizationId: number, payload: UserCreate): Promise<User> => {
    const response = await api.post(`/api/organizations/${organizationId}/users/`, {
      ...payload,
      organization_id: organizationId,
    });
    return response.data;
  },
  createWithFirebase: async (
    organizationId: number,
    payload: UserCreateWithFirebase
  ): Promise<User> => {
    const response = await api.post(
      `/api/organizations/${organizationId}/users/create-with-firebase`,
      { ...payload, organization_id: organizationId }
    );
    return response.data;
  },
  update: async (organizationId: number, userId: number, payload: UserUpdate): Promise<User> => {
    const response = await api.put(`/api/organizations/${organizationId}/users/${userId}`, payload);
    return response.data;
  },
  setProjects: async (organizationId: number, userId: number, payload: UserProjectAssignment): Promise<User> => {
    const response = await api.post(`/api/organizations/${organizationId}/users/${userId}/projects`, payload);
    return response.data;
  },
  activate: async (organizationId: number, userId: number): Promise<User> => {
    const response = await api.post(`/api/organizations/${organizationId}/users/${userId}/activate`);
    return response.data;
  },
  deactivate: async (organizationId: number, userId: number): Promise<User> => {
    const response = await api.post(`/api/organizations/${organizationId}/users/${userId}/deactivate`);
    return response.data;
  },
  delete: async (organizationId: number, userId: number): Promise<void> => {
    await api.delete(`/api/organizations/${organizationId}/users/${userId}`);
  },
  setRole: async (organizationId: number, userId: number, role: 'ADMIN' | 'MEMBER'): Promise<User> => {
    const response = await api.post(`/api/organizations/${organizationId}/users/${userId}/role`, { role });
    return response.data;
  },
  sync: async (organizationId: number, payload: UserSync): Promise<User> => {
    const response = await api.post(`/api/organizations/${organizationId}/users/sync`, {
      ...payload,
      organization_id: organizationId,
    });
    return response.data;
  },
};

// Organization Settings API
export const organizationSettingsApi = {
  get: async (organizationId: number): Promise<OrganizationSettings> => {
    const response = await api.get(`/api/organizations/${organizationId}/settings`);
    return response.data;
  },
  create: async (organizationId: number, settings: OrganizationSettingsCreate): Promise<OrganizationSettings> => {
    const response = await api.post(`/api/organizations/${organizationId}/settings`, settings);
    return response.data;
  },
  update: async (organizationId: number, settings: OrganizationSettingsUpdate): Promise<OrganizationSettings> => {
    const response = await api.put(`/api/organizations/${organizationId}/settings`, settings);
    return response.data;
  },
  delete: async (organizationId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/api/organizations/${organizationId}/settings`);
    return response.data;
  },
};

// Phase 1: Document Processing API
export const documentsApi = {
  // Analyze (upload and process) a document
  analyzeDocument: async (
    file: File, 
    organizationId: number, 
    projectId: number, 
    enableEnhancedAnalysis: boolean = false
  ): Promise<DocumentProcessingResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('organization_id', organizationId.toString());
    formData.append('project_id', projectId.toString());
    formData.append('enable_enhanced_analysis', enableEnhancedAnalysis.toString());
    
    const response = await api.post('/api/test-cases/analyze-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Get processed document with summary, sections, and logs
  getDocument: async (documentId: number): Promise<ProcessedDocumentResponse> => {
    const response = await api.get(`/api/documents/${documentId}`);
    return response.data;
  },

  // Get document sections
  getSections: async (documentId: number, level?: number): Promise<DocumentSection[]> => {
    const params = level !== undefined ? { level } : {};
    const response = await api.get(`/api/documents/${documentId}/sections`, { params });
    return response.data;
  },

  // Get document entities with filters
  getEntities: async (documentId: number, filters?: EntityFilters): Promise<DocumentEntity[]> => {
    const response = await api.get(`/api/documents/${documentId}/entities`, { params: filters });
    return response.data;
  },

  // Get document tables
  getTables: async (documentId: number, filters?: TableFilters): Promise<DocumentTable[]> => {
    const response = await api.get(`/api/documents/${documentId}/tables`, { params: filters });
    return response.data;
  },

  // Get document references
  getReferences: async (documentId: number, filters?: ReferenceFilters): Promise<DocumentReference[]> => {
    const response = await api.get(`/api/documents/${documentId}/references`, { params: filters });
    return response.data;
  },

  // Get document topics
  getTopics: async (documentId: number): Promise<DocumentTopic[]> => {
    const response = await api.get(`/api/documents/${documentId}/topics`);
    return response.data;
  },

  // Get document summary
  getSummary: async (documentId: number): Promise<DocumentSummary> => {
    const response = await api.get(`/api/documents/${documentId}/summary`);
    return response.data;
  },

  // Delete document (cascade delete + file cleanup)
  deleteDocument: async (documentId: number): Promise<{ message: string; document_id: number }> => {
    const response = await api.delete(`/api/documents/${documentId}`);
    return response.data;
  },

  // List documents by organization and project
  listByProject: async (
    organizationId: number, 
    projectId: number, 
    status?: string
  ): Promise<DocumentListItem[]> => {
    const params = status ? { status } : {};
    const response = await api.get(`/api/documents/organization/${organizationId}/project/${projectId}`, { params });
    return response.data;
  },
};
// =====================================================
// CONTENT API - Intelligent Recommendations
// =====================================================

export interface CategoryRecommendation {
  category_id: number;
  category_name: string;
  domain_name: string;
  score: number;
  confidence: 'high' | 'medium' | 'low';
  matched_signals: Array<{
    signal_name: string;
    signal_count: number;
    min_required: number;
    weight: number;
    score: number;
  }>;
  missing_required_signals: string[];
  is_recommended: boolean;
}

export interface ContentRecommendationsResponse {
  content_id: number;
  recommendations: CategoryRecommendation[];
  total_recommended: number;
}

export const contentApi = {
  // Get recommended categories based on content analysis
  recommendCategories: async (contentId: number, limit: number = 10): Promise<ContentRecommendationsResponse> => {
    const response = await api.get(`/api/content/recommend-categories/${contentId}`, {
      params: { limit }
    });
    return response.data;
  },

  // Get content signal summary
  getSignals: async (contentId: number) => {
    const response = await api.get(`/api/content/signals/${contentId}`);
    return response.data;
  },
};
